import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import { toast } from "react-toastify";
import { MdEdit, MdDelete } from "react-icons/md";
import { FaPlusSquare, FaFileDownload } from "react-icons/fa";
import PaymentEditModal from "@modals/PaymentEditModal";
import InvoiceDetailModal from "@modals/InvoiceDetailModal";
import Helpers from "@/Helpers";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

export default function CompanyBillingPayments(props) {
  const { tabData, updateSpinner, updateAccountingData } = props;
  const [data, setData] = useState({ payments: [], pendingInvoices: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tabData.company?.id]);

  async function fetchData() {
    if (!tabData.company?.id) return;

    setLoading(true);
    setError(null);
    updateSpinner && updateSpinner(true);

    try {
      const resp = await RightTimeApi.getAsync(`/Company/${tabData.company.id}/Payments`);
      if (resp && resp.ok && resp.jsonData) {
        setData(resp.jsonData);
      } else {
        setError("Failed to load payment data");
        console.error("Failed to load payment data:", resp.status, resp.statusText);
      }
    } catch (error) {
      console.error("Error loading payment data:", error);
      setError("Error loading payment data: " + error.message);
    } finally {
      setLoading(false);
      updateSpinner && updateSpinner(false);
    }
  }

  function handleCreateNewPayment() {
    setEditingPaymentId(0);
  }

  function handleEditPayment(paymentId) {
    setEditingPaymentId(paymentId);
  }

  function handlePaymentEditModalClose() {
    setEditingPaymentId(null);
    fetchData(); // Refresh data after changes
    if (updateAccountingData) {
      updateAccountingData(); // Refresh parent accounting data
    }
  }

  function handleInvoiceClick(invoiceId) {
    setSelectedInvoiceId(invoiceId);
  }

  function handleInvoiceDetailModalClose() {
    setSelectedInvoiceId(null);
    fetchData(); // Refresh data after changes
    if (updateAccountingData) {
      updateAccountingData(); // Refresh parent accounting data
    }
  }

  async function handleDeletePayment(paymentId) {
    if (!window.confirm("Are you sure you want to delete this payment?")) {
      return;
    }

    updateSpinner && updateSpinner(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/Payment/${paymentId}`);
      if (resp && resp.ok) {
        toast.success("Payment deleted successfully");
        fetchData();
        if (updateAccountingData) {
          updateAccountingData();
        }
      } else {
        toast.error("Failed to delete payment");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Error deleting payment: " + error.message);
    } finally {
      updateSpinner && updateSpinner(false);
    }
  }

  async function handleDownloadPdf(e, payment) {
    e.stopPropagation(); // Prevent row click event
    
    if (!payment.invoiceId || !tabData.company?.id) {
      toast.error("Unable to download PDF - missing invoice or company ID");
      return;
    }

    try {
      updateSpinner && updateSpinner(true);
      const response = await RightTimeApi.getAsync(`/Company/${tabData.company.id}/Invoice/${payment.invoiceId}/Pdf`);
      const ok = await downloadResponseAsFile(response, `invoice_${payment.invoiceId}.pdf`);
      if (ok) toast.success("Invoice PDF downloaded successfully");
      else toast.error("Failed to download invoice PDF");
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error("Error downloading invoice PDF");
    } finally {
      updateSpinner && updateSpinner(false);
    }
  }

  if (loading) {
    return (
      <div className="center">
        <p>Loading payments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="center">
        <p className="error">{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="horizontal">
      <div className="tableWrapper factTable scrollable">
        <table>
          <caption>
            <span className="title">Pending Invoices</span>
            {data.pendingInvoices?.length > 0 && <span className="captionCount">({data.pendingInvoices.length})</span>}
          </caption>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Amount</th>
              <th>Balance Due</th>
              <th>Status</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            {data.pendingInvoices?.length > 0 ? (
              data.pendingInvoices.map((invoice) => (
                <tr 
                  key={invoice.id}
                  onClick={() => handleInvoiceClick(invoice.id)}
                  className="clickableRow"
                  title="Click to view invoice"
                >
                  <td><span>{invoice.id}</span></td>
                  <td>{Helpers.formatDate(invoice.invoiceDate)}</td>
                  <td>{Helpers.formatDate(invoice.dueDate)}</td>
                  <td className="right">{Helpers.formatCurrency(invoice.totalAmount)}</td>
                  <td className="right">{Helpers.formatCurrency(invoice.balanceDue || invoice.totalAmount)}</td>
                  <td>{invoice.status || 'Pending'}</td>
                  <td className="center">
                    <FaFileDownload 
                      className="actionIcon downloadIcon clickableIcon"
                      onClick={(e) => handleDownloadPdf(e, { invoiceId: invoice.id })}
                      title="Download Invoice PDF"
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="center">No pending invoices found for this company</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="tableWrapper factTable scrollable">
        <table>
          <caption>
            <span className="title">Company Payments</span>
            {data.payments?.length > 0 && <span className="captionCount">({data.payments.length})</span>}
            <span
              className="actionIcon plusIcon floatRight clickableIcon"
              onClick={handleCreateNewPayment}
              title="Create New Payment"
            >
              <FaPlusSquare />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Payment #</th>
              <th>Payment Date</th>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {data.payments?.length > 0 ? (
              data.payments.map((payment) => (
                <tr 
                  key={payment.id}
                  onClick={() => handleEditPayment(payment.id)}
                  className="clickableRow"
                  title="Click to edit payment"
                >
                  <td>{payment.id}</td>
                  <td>{Helpers.formatDate(payment.paymentDate)}</td>
                  <td className="right">{Helpers.formatCurrency(payment.amount)}</td>
                  <td>{payment.paymentMethod || 'N/A'}</td>
                  <td>{payment.paymentReference || 'N/A'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="center">No payments found for this company</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Payment Edit Modal */}
      {editingPaymentId !== null && (
        <PaymentEditModal
          paymentId={editingPaymentId}
          companyId={tabData.company?.id}
          onClose={handlePaymentEditModalClose}
          updateSpinner={updateSpinner}
        />
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoiceId !== null && (
        <InvoiceDetailModal
          invoiceId={selectedInvoiceId}
          companyId={tabData.company?.id}
          onClose={handleInvoiceDetailModalClose}
          updateSpinner={updateSpinner}
        />
      )}
    </div>
  );
}

